/**
 * confirmation-page-view — maps the public confirmation-page response onto the
 * canvas's inputs, for the CUSTOMER surface (`src/manage`, `manage.<tenant>`).
 *
 * Written by the API side as the implementation spec (2026-09-04). This is the
 * other half of the seam: the designer authors a page per service; the customer
 * opens `manage.<tenant>/<bookingId>` and the page they see is
 *
 *     const res = await getPublicBookingConfirmationPage(bookingId);   // no session
 *     <CSCanvas state={res.data.page.sections} type={toCSJourneyType(res)}
 *               booking={toCSBooking(res)} fields={toCSPageFields(res)}
 *               pageState={toCSPageState(res)} surface="linked" ... />
 *
 * ONE CALL, NO SESSION. The booking id in the path is the credential; the lane
 * answers the masked stage-1 view and needs nothing from `manage-session.ts`.
 * So this can load in parallel with the session exchange and render before it
 * finishes — the policy-shaped view (reschedule, cancel, step-up) stays exactly
 * as it is, below or beside, wherever the design puts it.
 *
 * ONE LANE, TWO KINDS OF BOOKING (2026-09-11). The same operation now answers
 * an EVENT REGISTRATION as well as an appointment: the events-owned door
 * resolves the tenant's default page (an event has no service, so `serviceId`
 * is absent — nothing here pretends otherwise) and fills the same summary with
 * the registration's own fields. `journeyType` on the booking is what says
 * which kind arrived, and `toCSJourneyType` is the one place that reads it.
 *
 * THE STATE IS DERIVED, NEVER SENT. There is no `state` field on the wire: the
 * page reads it off `status`, `endsAt` and `cancellableUntil`, the same way it
 * will in production. `expired` is the 404 — there is no booking to map — and
 * the caller renders it from the refusal (see `isLinkInvalid`).
 *
 * `toCSBooking` fills only what the wire carries. Since the 2026-09-05 kit that
 * includes WHO THE PAGE IS FROM — `data.tenant.name` for the brand header and
 * `data.tenant.address` as the first footer line — because this lane renders
 * before any session exists and the canvas otherwise printed "Your tenant".
 * `promo`, `prepare`, `answers` come from the page document's interior or the
 * session, as the design decides; `guests` comes off the wire for an event
 * (the other seat holders, already masked when masking is on). Nothing here
 * invents a value.
 */

import { isApiError } from "@v0y4ge/api-client";
import type { PublicConfirmationPageResponse } from "@v0y4ge/api-contracts";
import type { CSBooking, CSPageFields, CSPageState } from "../screens/journeys/confirmation-page";
import type { CSType } from "../screens/journeys/confirmation-sections";

type Res = PublicConfirmationPageResponse;

/** `true` for the one refusal the page renders as a state rather than an error. */
export function isLinkInvalid(e: unknown): boolean {
  return isApiError(e) && e.code === "CONFIRMATION_PAGE_LINK_INVALID";
}

/** The journey type of THIS booking, in the canvas's vocabulary (2026-09-11).
 *
 *  Read off the response — the booking came from one funnel and the server
 *  says which — and never trusted blindly: an absent or unrecognised value
 *  falls back to the type the surface asked for, so a backend that does not
 *  send it yet renders the page it always did. The manage session already
 *  identified the journey type before this lane is read, so `requested` is
 *  never a guess. */
export function toCSJourneyType(res: Res, requested: CSType = "appointment"): CSType {
  const jt = res.data.booking.journeyType;
  return jt === "appointment" || jt === "event" || jt === "queue" ? jt : requested;
}

/** What happened to the booking, in the canvas's vocabulary. */
export function toCSPageState(res: Res, now: number = Date.now()): CSPageState {
  const b = res.data.booking;
  if (b.status === "cancelled") return "cancelled";
  if (b.status === "rescheduled") return "rescheduled";
  if (b.status === "completed" || b.status === "no_show") return "past";
  if (b.endsAt && Date.parse(b.endsAt) < now) return "past";
  // Inside the page's cancellation window: confirmed, but changes are closed.
  if (b.cancellableUntil && Date.parse(b.cancellableUntil) < now) return "closed";
  return "confirmed";
}

/** The event registration's own fields (2026-09-11), agreed with the API line
 *  as OPTIONAL members of `PublicBookingSummary` — `eventName`, `ticketType`,
 *  `seats`, `seatHolderNames` — served through the SAME public lane. They are
 *  read off the summary's open interior with a type check per field rather
 *  than a cast of the whole record: the summary is `.passthrough()`, so this
 *  works whether or not the kit's contract copy has caught up with the
 *  declarations, and a malformed value renders as absent rather than as
 *  "[object Object]". `seatHolderNames` arrives ALREADY MASKED when masking is
 *  on — nothing here masks or unmasks, same rule as `customerName`. Absent on
 *  an appointment, so an appointment page is unchanged. */
function toEventFields(b: Res["data"]["booking"]): {
  eventName?: string; ticketType?: string; seats?: number; seatHolderNames?: string[];
} {
  const r = b as Record<string, unknown>;
  const str = (v: unknown): string | undefined => (typeof v === "string" && v.trim() !== "" ? v : undefined);
  const seats = typeof r.seats === "number" && Number.isFinite(r.seats) && r.seats > 0 ? Math.floor(r.seats) : undefined;
  const names = Array.isArray(r.seatHolderNames)
    ? (r.seatHolderNames as unknown[]).filter((n): n is string => typeof n === "string" && n.trim() !== "")
    : [];
  return {
    eventName: str(r.eventName),
    ticketType: str(r.ticketType),
    seats,
    ...(names.length ? { seatHolderNames: names } : {}),
  };
}

/** The reception check-in code on the public confirmation summary (2026-09-15):
 *  optional `booking.checkInCode`, exactly the stored `check_in_code`. Read off
 *  the open summary with a type check, like the event fields above, and returned
 *  unchanged; a missing, blank or non-string value is absent. Never derived from
 *  the reference or the booking id. */
function toCheckInCode(b: Res["data"]["booking"]): string | undefined {
  const v = (b as Record<string, unknown>).checkInCode;
  return typeof v === "string" && v.trim() !== "" ? v : undefined;
}

/** The booking's own fields, formatted in the booking's own zone. */
export function toCSBooking(res: Res, locale: string = "en-GB"): CSBooking {
  const b = res.data.booking;
  const tz = b.timezone || "UTC";
  const start = b.startsAt ? new Date(b.startsAt) : null;
  const end = b.endsAt ? new Date(b.endsAt) : null;
  const date = new Intl.DateTimeFormat(locale, { timeZone: tz, weekday: "long", day: "numeric", month: "long", year: "numeric" });
  const time = new Intl.DateTimeFormat(locale, { timeZone: tz, hour: "2-digit", minute: "2-digit" });
  const minutes = start && end ? Math.round((end.getTime() - start.getTime()) / 60_000) : null;
  const tenant = res.data.tenant;
  const ev = toEventFields(b);
  /* The summary's "Tickets" row: how many and of what kind, from the two
     fields the wire carries. Either alone still reads as a line; neither
     means no row. */
  const ticketLine = ev.seats !== undefined && ev.ticketType
    ? `${ev.seats} × ${ev.ticketType}`
    : ev.seats !== undefined
    ? `${ev.seats} ${ev.seats === 1 ? "ticket" : "tickets"}`
    : ev.ticketType;
  return {
    // Who the page is from — the tenant's company root, never the venue.
    brandName: tenant?.name ?? undefined,
    footerLines: tenant?.address ? [tenant.address] : undefined,
    serviceName: b.serviceName,
    dateLine: start ? date.format(start) : undefined,
    timeLine: start && end ? `${time.format(start)} – ${time.format(end)}` : start ? time.format(start) : undefined,
    durationLine: minutes !== null ? `${minutes} min` : undefined,
    // Tenant data, never masked: the "With" line and the place.
    advisor: b.staffName,
    placeName: b.locationName,
    address: b.locationAddress,
    reference: b.reference,
    /* The booking's own RECORD ID (CD, 2026-09-18). Required on this response,
       so it is always there — unlike `reference` and `checkInCode`, both of which
       an anonymous confirmation can legitimately arrive without. It is what the
       reference target now encodes, for all three journey types. */
    bookingId: b.bookingId,
    // What the check-in code section encodes for the reception target. Absent
    // means no code is drawn on the live page.
    ...(toCheckInCode(b) !== undefined ? { checkInCode: toCheckInCode(b) } : {}),
    /* Unformatted, so the summary can print the DATE FORMAT the page chose.
       `dateLine` above stays the host's own formatting and is what renders when
       no format is chosen — the two never disagree, because the chosen format
       replaces the line rather than being applied on top of it. */
    ...(start ? { startISO: start.toISOString() } : {}),
    ...(end ? { endISO: end.toISOString() } : {}),
    timezone: tz,
    /* The canonical manage URL is projected by the server from the booking id
       and the public manage base. Never read the browser address: previews and
       funnels can wrap this page in a different URL. */
    manageUrl: b.manageUrl ?? null,
    // Event registration (2026-09-11): the event, the tickets and the other
    // seat holders, for the summary and guests sections. All absent on an
    // appointment, so nothing above changes for one.
    eventName: ev.eventName,
    ticketLine,
    guests: ev.seatHolderNames,
  };
}

/** The page-level fields the sections read, off the resolved document. The
 *  interior keys (`bannerTitle`, `note`, `surveyPrompt`) come through the
 *  passthrough under their own names. */
export function toCSPageFields(res: Res): CSPageFields {
  const p = res.data.page as Record<string, unknown> & Res["data"]["page"];
  const hours = p.cancellationWindow?.hours;
  return {
    bannerTitle: p.bannerTitle as string | undefined,
    bannerSub: p.bannerSub as string | undefined,
    cancelHours: typeof hours === "number" ? String(hours) : undefined,
    note: p.note as string | undefined,
    surveyPrompt: p.surveyPrompt as string | undefined,
    /* CONTRACTED since the platform shipped `rebookingWindow` (2026-09-14):
       null/absent = follows the cancellation window, {hours: 0} = open until
       the start. The interior key is read as a fallback, for the pages authored
       in the afternoon before that field existed. */
    rebookHours: typeof (p.rebookingWindow as { hours?: number } | null | undefined)?.hours === "number"
      ? String((p.rebookingWindow as { hours: number }).hours)
      : (p.rebookHours as string | undefined),
    downloadsOff: Array.isArray(p.downloadsOff) ? (p.downloadsOff as string[]) : undefined,
    summaryOff: Array.isArray(p.summaryOff) ? (p.summaryOff as string[]) : undefined,
    summaryLabels: (p.summaryLabels && typeof p.summaryLabels === "object" ? p.summaryLabels : undefined) as Record<string, string> | undefined,
    summaryOrder: Array.isArray(p.summaryOrder) ? (p.summaryOrder as string[]) : undefined,
    summaryDateFormat: p.summaryDateFormat as string | undefined,
    summaryTimeFormat: p.summaryTimeFormat as string | undefined,
    checkinCodeTarget: p.checkinCodeTarget === "manageUrl" ? "manageUrl" : "reference",
  };
}

/** What the customer may do from this page, for the downloads section. The
 *  values are server-owned flags — render them, never probe. */
export function toActions(res: Res): { summaryPdf: boolean; walletPass: boolean } {
  return { summaryPdf: res.data.actions.summaryPdfEnabled, walletPass: res.data.actions.walletPassEnabled };
}

/** Whether the name on the page is masked, and which fields are — read it here,
 *  never infer it from the value. `stepUp.channels` may legitimately be empty. */
export function toMasking(res: Res): { masked: boolean; fields: string[]; stepUpRequired: boolean; channels: string[] } {
  return {
    masked: res.data.masking.enabled,
    fields: res.data.masking.fields,
    stepUpRequired: res.data.stepUp.required,
    channels: res.data.stepUp.channels,
  };
}
